/**
 * Try it with no key: a sandbox from POST /v1/sandbox (cloud/src/core/sandbox.ts).
 *
 * Where the API asks for a human check, Turnstile runs on /signup and nowhere
 * else (DESIGN.md §8): this page sends the visitor there (`#try=1`), and the
 * check sends them back to `#/try?token=…`. Where it asks for none (dev), the
 * sandbox is made at once. The sandbox's key is held in memory only
 * (session.setSandbox): a reload drops it, and Connect offers a fresh one.
 *
 * Pure, so `bun test` runs it with fake calls.
 */

import type { SandboxInfo } from '../contracts';

/** Where the human check for a sandbox runs: the sign-up page, in its try mode. */
export const TRY_CHECK_HREF = '/signup#try=1';

type Json = unknown;
const isObj = (v: Json): v is Record<string, Json> => typeof v === 'object' && v !== null && !Array.isArray(v);

export interface SandboxConfigView { readonly available: boolean; readonly turnstileSitekey: string | null }

/** GET /v1/sandbox/config's answer, or null when it is not one. */
export const sandboxConfigOf = (body: Json): SandboxConfigView | null =>
  isObj(body) && typeof body.available === 'boolean'
    ? { available: body.available, turnstileSitekey: typeof body.turnstileSitekey === 'string' && body.turnstileSitekey ? body.turnstileSitekey : null }
    : null;

export interface MadeSandbox extends SandboxInfo { readonly secret: string; readonly worlds: readonly string[] }

/** POST /v1/sandbox's 201, or null when it is not one. */
export const madeSandboxOf = (body: Json): MadeSandbox | null => {
  if (!isObj(body) || typeof body.workspace !== 'string' || typeof body.expiresAt !== 'number' || !isObj(body.key)) return null;
  const secret = body.key.secret;
  if (typeof secret !== 'string' || !/^sbk_[A-Za-z0-9_]+$/.test(secret)) return null;
  const worlds = Array.isArray(body.worlds) ? body.worlds.filter((w): w is string => typeof w === 'string') : [];
  return { workspace: body.workspace, expiresAt: body.expiresAt, secret, worlds };
};

export interface Answer { readonly status: number; readonly body: Json }

export interface TryDeps {
  config(): Promise<Answer>;
  mint(body: Record<string, Json>): Promise<Answer>;
}

export type TryOutcome =
  | { readonly kind: 'made'; readonly sandbox: MadeSandbox }
  /** The API asks for a human check and none came with this try: go to TRY_CHECK_HREF. */
  | { readonly kind: 'check' }
  | { readonly kind: 'problem'; readonly status: number; readonly detail: string };

const problemOf = (answer: Answer): TryOutcome => {
  const b = answer.body;
  const detail = isObj(b) && typeof b.detail === 'string' ? b.detail
    : answer.status === 0 ? 'the API could not be reached' : `the API answered ${answer.status}`;
  return { kind: 'problem', status: answer.status, detail };
};

/**
 * One try: ask whether a check is needed, then make the sandbox. A token from
 * the check is good once, so a try that carries one never asks for another.
 */
export const tryOnce = async (deps: TryDeps, token: string | null): Promise<TryOutcome> => {
  if (!token) {
    const asked = await deps.config();
    const config = asked.status === 200 ? sandboxConfigOf(asked.body) : null;
    if (!config) return problemOf(asked);
    if (!config.available) return { kind: 'problem', status: 503, detail: 'sandboxes are not offered on this API right now; bring a key, or sign up' };
    if (config.turnstileSitekey) return { kind: 'check' };
  }
  const answer = await deps.mint(token ? { turnstileToken: token } : {});
  const made = answer.status === 201 ? madeSandboxOf(answer.body) : null;
  return made ? { kind: 'made', sandbox: made } : problemOf(answer);
};

/** "sandbox · expires in 1h 52m", "… in 4m", "… lapsed" (counted down by the HUD). */
export const badgeText = (expiresAt: number, now: number): string => {
  const left = expiresAt - now;
  if (left <= 0) return 'sandbox · lapsed';
  const minutes = Math.ceil(left / 60_000);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `sandbox · expires in ${h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`}`;
};

/** A website link like `/?try=1` starts the try: the hash route it becomes, or null when the search asks nothing. */
export const tryFromSearch = (search: string): string | null =>
  new URLSearchParams(search).get('try') === '1' ? '#/try' : null;

/**
 * A try the route asked for, handed from main.ts to the Connect screen: the
 * route becomes Connect at once, so the check's token leaves the address bar
 * before it is spent, and a re-render can't spend it twice. `undefined`: none
 * waiting; `null`: a try with no token.
 */
let handed: string | null | undefined;
export const handTry = (token: string | null): void => { handed = token; };
export const takeTry = (): string | null | undefined => {
  const t = handed;
  handed = undefined;
  return t;
};
